import { QuotationStateProps } from './types';
import {STEPS} from "./lib/consts";
import {FormattedMessage} from "react-intl";
import {QuotationUserSelector} from "@feature/quotations/quotation/components/quotation-user-selector";
import {useWorksiteUsers} from "@hooks/useWorksiteUsers";
import {fetchAgentEnterprises, fetchAgentResales} from "@feature/quotations/lib/utilities";
import apiRequest from "@services/apiRequest";
import {UserOption} from "@feature/commons";
import {UserReducedSchema} from "@feature/types";
import React, {useState, useEffect} from 'react';
import {QuotationUser} from "@feature/quotations";
import {useConnectedUsers} from "@feature/users/connections"
import {mapEnterprisesOptions} from "../../../lib/utilities";

export const QuotationState = ({
  editable,
  worksite,
  status,
  users,
  onUserChange,
  submittingUserChange,
  onUserInvite,
  submittingUserInvite,
  defaultOptions,
}: QuotationStateProps) => {

  // hooks
  // @ts-ignore
  const [worksiteUsers]: [UserReducedSchema[]] = useWorksiteUsers(worksite);
  // const {
  //   fetch: fetchConnectedUsers,
  //   loading: loadingConnectedUsers,
  // } = useConnectedUsers();

  // state
  const [enterprisesDefaultOptions, setEnterprisesDefaultOptions]
    = useState<UserOption[]>([]);
  const [resalesDefaultOptions, setResalesDefaultOptions]
    = useState<UserOption[]>([]);

  useEffect(() => {
    fetchAgentEnterprises(
      apiRequest,
      users.agent.id,
      undefined,
      10,
      false
    ).then((results) => {
      const dfo = mapEnterprisesOptions(results.items) as unknown as UserOption[];
      setEnterprisesDefaultOptions(dfo);
    })
    fetchAgentResales(
      apiRequest,
      users.agent.id,
      undefined,
      10,
      undefined,
      undefined,
      false
    ).then((results) => {
      const dro = mapEnterprisesOptions(results.items) as unknown as UserOption[];
      setResalesDefaultOptions(dro);
    })
  }, [users.agent.id]);

  // utilities
  const getCurrentIdx = () => {
    if (status === 'waiting_for_agent')
      return STEPS.indexOf('creation');
    if (status === 'waiting_for_resale')
      return STEPS.indexOf('revision');
    if (status === 'waiting_for_enterprise')
      return STEPS.indexOf('approval');
    return STEPS.length + 1;
  }
  const getBarClass = (idx: number) => {
    const currentIdx = getCurrentIdx();
    if (status === 'archived')
      // Bar must be red if the quotation has been archived (hard rejection)
      return 'bg-red-400';
    if (idx < currentIdx)
      return 'bg-kerakoll-700';
    if (idx === currentIdx)
      return 'bg-kerakoll-400 animate-pulse';
    return 'bg-gray-100';
  }
  const fetchUsersFn = async (
    name: string,
    userKey: QuotationUser,
  ) => {
    const promise: Promise<UserReducedSchema[]> = ['enterprise', 'resale'].includes(userKey)
      // CASE A - Fetch enterprises connected to the current
      ? userKey === 'enterprise'? (
        fetchAgentEnterprises(apiRequest, users.agent.id, name)
          .then((r) => r.items)
          .then((r) => mapEnterprisesOptions(r))
      ):(
        fetchAgentResales(apiRequest, users.agent.id, name)
          .then((r) => r.items)
          .then((r) => mapEnterprisesOptions(r) as UserReducedSchema[])
      )
      // CASE B - Fetch worksite users having the target role
      : new Promise((resolve) => {
        resolve(
          worksiteUsers.filter((u) =>
            u.name.toLowerCase().includes(name.toLowerCase())
            && (
              userKey === 'agent'
                ? u.role === 'agent'
                : u.role.startsWith('resale')
            )
          )
        );
      });

    return await promise.then((r) => {
      switch (userKey) {
        case 'enterprise':
          return r
        case 'resale':
          return r
        default:
          return r.map((u) => ({
            value: u.id,
            label: u.name,
          } as UserOption))
      }
    })
  }

  return <>
    <div className="flex flex-col gap-10 md:gap-6 p-6 pb-10 md:pb-6 border border-gray-200 rounded bg-white">
      <p className="text-xl font-semibold">
        <FormattedMessage
          id="quotation_state"
          defaultMessage="Stato del preventivo"
        />
      </p>
      <nav aria-label="Progress">
        <ol role="list" className="space-y-10 md:flex md:space-x-10 md:space-y-0">
          {['creation', 'revision', 'approval'].map((s, idx, arr) => {

            const userKey = s === 'creation'
              ? 'agent'
              : s === 'revision'
                ? 'resale'
                : 'enterprise';  // approval
            const barClass = getBarClass(idx);

            return <React.Fragment key={idx}>
              <li className="md:flex-1">
                <div className="flex flex-col gap-4">
                  <div className={`h-1.5 ${barClass}`}></div>
                  <div className="flex flex-col gap-1">
                    <div className="text-base">
                      <span className="font-medium">
                        Step {idx + 1} &ndash; <FormattedMessage id={`quotation_state_${s}`}/>
                      </span>
                    </div>
                    <div className="font-light text-xs">
                      <FormattedMessage id={`quotation_state_${s}_description`}/>
                    </div>
                  </div>
                  <hr className="mt-2" />
                  <QuotationUserSelector
                    editable={editable}
                    label={userKey}
                    value={{
                      label: users[userKey].name,
                      value: users[userKey].id,
                    } as UserOption}
                    fixed={users[userKey].fixed}
                    fetch={(search: string) => fetchUsersFn(search, userKey)}
                    onSelect={(value) => onUserChange(userKey, value)}
                    submittingSelection={submittingUserChange === userKey}
                    onInvite={() => onUserInvite(userKey)}
                    submittingInvitation={submittingUserInvite === userKey}
                    userKey={userKey}
                    defaultOptions={userKey === 'enterprise' ? enterprisesDefaultOptions
                      : userKey === 'resale' ? resalesDefaultOptions : undefined}
                  />
                </div>
              </li>
            </React.Fragment>
          })}
        </ol>
      </nav>
    </div>
  </>
}